import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import type { Translation } from 'primeng/api';

/** Adaptador de PrimeNG: no define una segunda paleta, consume los tokens CSS de RBI. */
function buttonSeverity(color: string, hover: string, contrast: string) {
    return {
        background: color,
        hoverBackground: hover,
        activeBackground: hover,
        borderColor: color,
        hoverBorderColor: hover,
        activeBorderColor: hover,
        color: contrast,
        hoverColor: contrast,
        activeColor: contrast,
        focusRing: { color, shadow: 'var(--focus-ring-shadow)' },
    };
}

// Aura conserva su escala (0 claro → 950 oscuro); RBI invierte sus roles en modo oscuro.
function surfaceToken(light: number, dark: number): string {
    return `light-dark(var(--surface-${light}), var(--surface-${dark}))`;
}

const secondaryButton = buttonSeverity('var(--surface-100)', 'var(--surface-200)', 'var(--text-color)');
const buttonTones = {
    primary: { color: 'var(--control-accent)', background: 'var(--control-accent-soft)' },
    secondary: { color: 'var(--text-color-secondary)', background: 'var(--surface-highlight)' },
    success: { color: 'var(--success-text)', background: 'var(--success-bg)' },
    warn: { color: 'var(--warning-text)', background: 'var(--warning-bg)' },
    danger: { color: 'var(--danger-text)', background: 'var(--danger-bg)' },
    info: { color: 'var(--info-text)', background: 'var(--info-bg)' },
    help: { color: 'var(--audit-text)', background: 'var(--brand-audit-light)' },
    contrast: { color: 'var(--text-color)', background: 'var(--surface-100)' },
};

const textButtons = Object.fromEntries(Object.entries(buttonTones).map(([tone, tokens]) => [tone, {
    color: tokens.color,
    hoverBackground: tokens.background,
    activeBackground: tokens.background,
}]));
const outlinedButtons = Object.fromEntries(Object.entries(textButtons).map(([tone, tokens]) => [tone, {
    ...tokens,
    borderColor: 'var(--surface-border-strong)',
}]));

const selectOverlay = {
    background: 'var(--popup-bg)',
    borderColor: 'var(--surface-border-strong)',
    color: 'var(--text-color)',
    borderRadius: 'var(--radius-lg)',
    shadow: 'var(--shadow-popup)',
};

function feedbackTone(tokens: { color: string; background: string }) {
    return {
        background: 'var(--surface-card-solid)', borderColor: tokens.color, color: tokens.color,
        detailColor: 'var(--text-color)', shadow: 'var(--shadow-toast)',
        closeButton: { hoverBackground: tokens.background, focusRing: { color: tokens.color, shadow: 'var(--focus-ring-shadow)' } },
    };
}

export const RBI_PRIMENG_PRESET = definePreset(Aura, {
    semantic: {
        typography: {
            fontFamily: 'var(--font-family-base)', fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-normal)', lineHeight: 'var(--line-height-normal)',
        },
        transitionDuration: 'var(--transition-duration)',
        disabledOpacity: 'var(--disabled-opacity)',
        iconSize: 'var(--font-size-sm)',
        focusRing: {
            width: 'var(--focus-ring-width)', style: 'solid', color: 'var(--control-accent)',
            offset: 'var(--focus-ring-offset)', shadow: 'var(--focus-ring-shadow)',
        },
        primary: {
            50: 'var(--primary-50)', 100: 'var(--primary-100)', 200: 'var(--primary-200)',
            300: 'var(--primary-300)', 400: 'var(--primary-400)', 500: 'var(--primary-500)',
            600: 'var(--primary-600)', 700: 'var(--primary-700)', 800: 'var(--primary-800)',
            900: 'var(--primary-900)', 950: 'var(--primary-900)',
            color: 'var(--control-accent)', contrastColor: 'var(--control-accent-contrast)',
            hoverColor: 'var(--control-accent-hover)', activeColor: 'var(--control-accent-hover)',
        },
        surface: {
            0: surfaceToken(0, 900), 50: surfaceToken(50, 800), 100: surfaceToken(100, 700),
            200: surfaceToken(200, 600), 300: surfaceToken(300, 500), 400: surfaceToken(400, 400),
            500: surfaceToken(500, 300), 600: surfaceToken(600, 200), 700: surfaceToken(700, 100),
            800: surfaceToken(800, 50), 900: surfaceToken(900, 0), 950: surfaceToken(900, 50),
        },
        text: {
            color: 'var(--text-color)', hoverColor: 'var(--text-color)',
            mutedColor: 'var(--text-color-secondary)', hoverMutedColor: 'var(--text-color)',
        },
        highlight: {
            background: 'var(--control-accent-soft)', focusBackground: 'var(--control-accent-soft-border)',
            color: 'var(--text-color)', focusColor: 'var(--text-color)',
        },
        formField: {
            paddingX: 'var(--control-padding-x)', paddingY: 'var(--control-padding-y)',
            fontSize: 'var(--control-font-size)', borderRadius: 'var(--control-radius)',
            background: 'var(--surface-card-solid)', filledBackground: 'var(--surface-50)',
            filledHoverBackground: 'var(--surface-100)', filledFocusBackground: 'var(--surface-card-solid)',
            disabledBackground: 'var(--surface-100)', disabledColor: 'var(--text-color-secondary)',
            borderColor: 'var(--surface-border-strong)', hoverBorderColor: 'var(--control-accent)',
            focusBorderColor: 'var(--control-accent)', invalidBorderColor: 'var(--danger-color)',
            color: 'var(--text-color)', placeholderColor: 'var(--text-color-secondary)',
            invalidPlaceholderColor: 'var(--danger-color)', iconColor: 'var(--text-color-secondary)',
            floatLabelColor: 'var(--text-color-secondary)', floatLabelFocusColor: 'var(--control-accent)',
            floatLabelActiveColor: 'var(--text-color-secondary)', floatLabelInvalidColor: 'var(--danger-color)',
            shadow: 'none',
            focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'var(--focus-ring-shadow)' },
            sm: { fontSize: 'var(--font-size-xs)', paddingX: 'var(--control-padding-x)', paddingY: 'var(--control-padding-y)' },
            lg: { fontSize: 'var(--font-size-base)', paddingX: 'var(--control-padding-x)', paddingY: 'var(--control-padding-y)' },
        },
        content: {
            background: 'var(--surface-card-solid)', hoverBackground: 'var(--surface-highlight)',
            borderColor: 'var(--surface-border)', color: 'var(--text-color)', hoverColor: 'var(--text-color)',
            borderRadius: 'var(--radius-lg)',
        },
        mask: { background: 'var(--surface-overlay)' },
        list: {
            padding: 'var(--space-xs)', gap: 'var(--space-xs)',
            option: { padding: 'var(--space-sm)', focusBackground: 'var(--surface-highlight)' },
        },
        navigation: {
            item: { focusBackground: 'var(--surface-highlight)', activeBackground: 'var(--control-accent-soft)' },
        },
        overlay: {
            select: selectOverlay,
            popover: { ...selectOverlay, padding: 'var(--space-md)' },
            modal: { ...selectOverlay, padding: 'var(--space-md)', shadow: 'var(--shadow-dialog)' },
            navigation: { shadow: 'var(--shadow-popup)' },
        },
    },
    components: {
        toast: {
            root: { width: 'var(--toast-width)' },
            info: feedbackTone(buttonTones.info), success: feedbackTone(buttonTones.success),
            warn: feedbackTone(buttonTones.warn),
            error: {
                ...feedbackTone(buttonTones.danger),
                background: 'var(--toast-error-background)',
                borderColor: 'var(--toast-error-border)',
                color: 'var(--toast-error-color)',
                detailColor: 'var(--toast-error-color)',
                closeButton: {
                    hoverBackground: 'var(--toast-error-hover-background)',
                    focusRing: { color: 'var(--toast-error-color)', shadow: 'var(--focus-ring-shadow)' },
                },
            },
            secondary: feedbackTone(buttonTones.secondary),
        },
        button: {
            root: {
                paddingX: 'var(--button-padding-x)', paddingY: 'var(--button-padding-y)',
                borderRadius: 'var(--button-radius)', roundedBorderRadius: 'var(--radius-pill)',
                fontSize: 'var(--button-font-size)', gap: 'var(--button-icon-gap)',
                iconOnlyWidth: 'var(--icon-button-size)', label: { fontWeight: 'var(--font-weight-semibold)' },
                primary: buttonSeverity('var(--control-accent)', 'var(--control-accent-hover)', 'var(--control-accent-contrast)'),
                secondary: secondaryButton,
                success: buttonSeverity('var(--success-color-dark)', 'var(--success-color-dark)', 'var(--color-white)'),
                warn: buttonSeverity('var(--warning-color)', 'var(--warning-color-dark)', 'var(--brand-ink)'),
                danger: buttonSeverity('var(--danger-color)', 'var(--danger-color-dark)', 'var(--color-white)'),
                info: buttonSeverity('var(--info-color)', 'var(--info-color-dark)', 'var(--brand-ink)'),
                help: buttonSeverity('var(--brand-audit)', 'var(--brand-audit-dark)', 'var(--color-white)'),
                contrast: buttonSeverity('var(--text-color)', 'var(--text-color-secondary)', 'var(--surface-0)'),
                sm: { fontSize: 'var(--font-size-xs)', iconOnlyWidth: 'var(--icon-button-size)' },
            },
            text: textButtons,
            outlined: outlinedButtons,
        },
        dialog: { root: { borderRadius: 'var(--radius-xl)' }, header: { padding: 'var(--space-md)' } },
        tooltip: { root: { background: 'var(--tooltip-bg)', color: 'var(--color-white)', padding: 'var(--space-sm)', shadow: 'var(--tooltip-shadow)' } },
        datatable: {
            headerCell: { background: 'var(--table-header-bg)', color: 'var(--table-header-color)', padding: 'var(--table-header-padding-y) var(--table-header-padding-x)' },
            columnTitle: { fontSize: 'var(--table-header-font-size)' },
            bodyCell: { fontSize: 'var(--table-cell-font-size)', fontWeight: 'var(--font-weight-normal)', padding: 'var(--table-cell-padding-y) var(--table-cell-padding-x)', borderColor: 'var(--table-border-color)' },
            row: { stripedBackground: 'var(--surface-50)', background: 'var(--surface-card-solid)', hoverBackground: 'var(--table-row-hover-bg)', color: 'var(--text-color)', hoverColor: 'var(--text-color)' },
        },
    },
});

/** CSS de librería debajo de los estilos RBI; el reset no compite con los controles. */
export const RBI_PRIMENG_OPTIONS = {
    darkModeSelector: '.my-app-dark',
    cssLayer: { name: 'primeng', order: 'reset, primeng, rbi' },
};

export const RBI_OVERLAY_Z_INDEX = { modal: 1100, overlay: 1120, menu: 1120, tooltip: 1200 };

// PrimeNG trae sus textos en inglés. Sin esto, los menús de filtro de las
// tablas y los calendarios muestran "Clear" / "January" en una app en
// español. Solo se traduce lo que la app realmente usa.
export const RBI_PRIMENG_TRANSLATION: Translation = {
  clear: 'Limpiar',
  apply: 'Aplicar',
  matchAll: 'Cumplir todas',
  matchAny: 'Cumplir alguna',
  addRule: 'Agregar regla',
  removeRule: 'Quitar regla',
  startsWith: 'Empieza con',
  contains: 'Contiene',
  notContains: 'No contiene',
  endsWith: 'Termina con',
  equals: 'Es igual a',
  notEquals: 'No es igual a',
  noFilter: 'Sin filtro',
  lt: 'Menor que',
  lte: 'Menor o igual que',
  gt: 'Mayor que',
  gte: 'Mayor o igual que',
  is: 'Es',
  isNot: 'No es',
  before: 'Antes de',
  after: 'Después de',
  dateIs: 'La fecha es',
  dateIsNot: 'La fecha no es',
  dateBefore: 'La fecha es anterior a',
  dateAfter: 'La fecha es posterior a',
  accept: 'Sí',
  reject: 'No',
  choose: 'Elegir',
  upload: 'Subir',
  cancel: 'Cancelar',
  completed: 'Completado',
  pending: 'Pendiente',
  emptyMessage: 'Sin resultados',
  emptyFilterMessage: 'Sin coincidencias',
  dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
  dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
  monthNames: [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ],
  monthNamesShort: [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
  ],
  today: 'Hoy',
  weekHeader: 'Sem',
  firstDayOfWeek: 1,
  dateFormat: 'dd/mm/yy',
};
